import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Preference, SlackSettings } from "../../pages/Settings/types";
import { UserPreferenceItem } from "../../services/userPreferences";
import { GetPinnedProjectsResponse } from '../../types/apiResponse';

export interface OrganizationState {
    data: Record<string, unknown>[];
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    page: number;
    perPage: number;
    totalPages: number;
    totalItems: number;
    searchTerm: string;

    workItems: Record<string, unknown>[];
    workItemsLoading: boolean;
    workItemsLoadingMore: boolean;
    workItemsHasMore: boolean;
    workItemsPage: number;
    workItemsPerPage: number;
    workItemsTotalPages: number;
    workItemsTotalItems: number;
    workItemsSearchTerm: string;
    workItemsSearchType: 'all' | 'roadmap' | 'project' | 'epic' | 'feature';
    workItemsSortBy: string;
    workItemsSourceFilter: string;
    workItemsQuarterFilter: string[];
    workItemsPinnedFilter: boolean;
    workItemsPriorityFilter: string[];

    orgdata: Record<string, unknown>;
    orgDetails: Record<string, unknown>;
    skills: Record<string, unknown>[];
    recentProjects: Record<string, unknown>[];
    pinnedProjects: GetPinnedProjectsResponse[];
    pinnedProjectsIds: Record<string, unknown>[];
    skillLoading: boolean;
    recentProjectsLoading: boolean;
    skillError: string | null;
    error: string | null;
    orgUpdateError: string | null;
    recentProjectsError: string | null;
    orgUpdateSuccess: boolean;
    showSkillEdit: boolean;
    orgUpdateLoading: boolean;
    inviteUsersLoading: boolean;
    inviteUsersError: string | null;
    inviteUsersSuccess: boolean;
    jiraSettingsLoading: boolean;
    jiraSettingsSaveError: string | null;
    jiraSettingsSuccess: string | null;
    jiraSettingsError: string | null;
    asanaSettingsLoading: boolean;
    asanaSettingsSaveError: string | null;
    asanaSettingsSuccess: string | null;
    asanaSettingsError: string | null;
    projectReferenceLoading: boolean;
    projectReferenceError: string | null;
    projectReferenceData: Record<string, unknown>[];
    jiraSettings: Record<string, unknown>;
    asanaSettings: Record<string, unknown>;
    onboardSettings: Record<string, unknown>;
    onboardSettingsLoaded: boolean;
    orgPreferences: Preference[];
    orgPreferencesLoading: boolean;
    orgPreferencesError: string | null;
    orgWorkingDays: Record<string, unknown>[];
    orgWorkingDaysLoading: boolean;
    azureSettingsLoading: boolean;
    azureSettingsSaveError: string | null;
    azureSettingsSuccess: string | null;
    azureSettingsError: string | null;
    azureSettings: Record<string, unknown>;
    mondaySettingsLoading: boolean;
    mondaySettingsSaveError: string | null;
    mondaySettingsSuccess: string | null;
    mondaySettingsError: string | null;
    mondaySettings: Record<string, unknown>;
    orgSchedule: Record<string, unknown>[];
    orgScheduleLoading: boolean;
    orgScheduleError: string | null;
    showOrgScheduleEdit: boolean;
    activeIntegrationsLoading: boolean;
    activeIntegrations: { integration_type: string; is_active: boolean }[];
    teams: Record<string, unknown>[];
    teamsLoading: boolean;
    teamsError: string | null;
    teamsFetched: boolean;
    pendingInvites: Record<string, unknown>[];
    pendingInvitesLoading: boolean;
    pendingInvitesError: string | null;
    revokeInviteLoading: boolean;
    revokeInviteError: string | null;
    revokeInviteSuccess: boolean;
    githubSettings: Record<string, unknown>;
    githubSettingsLoading: boolean;
    githubSettingsError: string | null;
    userPreferences: UserPreferenceItem[];
    userPreferencesLoading: boolean;
    toleranceModes: Record<string, unknown>[];
    toleranceModesLoading: boolean;
    toleranceModesError: string | null;
    slackSettingsLoading: boolean;
    slackSettings: SlackSettings | null;
    slackSettingsError: string | null;
    azureStatesSyncRunning: boolean;
    jiraStatesSyncRunning: boolean;
}

const initialState: OrganizationState = {
    data: [] as Record<string, unknown>[],
    loading: false,
    loadingMore: false,
    hasMore: true,
    page: 1,
    perPage: 10,
    totalPages: 1,
    totalItems: 0,
    searchTerm: '',

    //combined API state (fetchRoadmapsAndProjects) - used by Work Items page
    workItems: [] as Record<string, unknown>[],
    workItemsLoading: false,
    workItemsLoadingMore: false,
    workItemsHasMore: true,
    workItemsPage: 1,
    workItemsPerPage: 10,
    workItemsTotalPages: 1,
    workItemsTotalItems: 0,
    workItemsSearchTerm: '',
    // Filter state for combined API (all prefixed with workItems)
    workItemsSearchType: 'all' as 'all' | 'roadmap' | 'project' | 'epic' | 'feature',
    workItemsSortBy: 'modified_at_desc' as string,
    workItemsSourceFilter: '' as string,
    workItemsQuarterFilter: [] as string[],
    workItemsPinnedFilter: false as boolean,
    workItemsPriorityFilter: [] as string[],

    orgdata: {},
    orgDetails: {} as Record<string, unknown>,
    skills: [] as Record<string, unknown>[],
    recentProjects: [] as Record<string, unknown>[],
    pinnedProjects: [] as GetPinnedProjectsResponse[],
    pinnedProjectsIds: [] as Record<string, unknown>[],
    skillLoading: false,
    recentProjectsLoading: false,
    skillError: null,
    error: null,
    orgUpdateError: null,
    recentProjectsError: null,
    orgUpdateSuccess: false,
    showSkillEdit: false,
    orgUpdateLoading: false,
    inviteUsersLoading: false,
    inviteUsersError: null,
    inviteUsersSuccess: false,
    jiraSettingsLoading: false,
    jiraSettingsSaveError: null,
    jiraSettingsSuccess: null,
    jiraSettingsError: null,
    asanaSettingsLoading: false,
    asanaSettingsSaveError: null,
    asanaSettingsSuccess: null,
    asanaSettingsError: null,
    projectReferenceLoading: false,
    projectReferenceError: null,
    projectReferenceData: [] as Record<string, unknown>[],
    jiraSettings: {},
    asanaSettings: {},
    onboardSettings: {},
    onboardSettingsLoaded: false,
    orgPreferences: [] as Preference[],
    orgPreferencesLoading: false,
    orgPreferencesError: null,
    orgWorkingDays: [] as Record<string, unknown>[],
    orgWorkingDaysLoading: false,
    azureSettingsLoading: false,
    azureSettingsSaveError: null,
    azureSettingsSuccess: null,
    azureSettingsError: null,
    azureSettings: {},
    mondaySettingsLoading: false,
    mondaySettingsSaveError: null,
    mondaySettingsSuccess: null,
    mondaySettingsError: null,
    mondaySettings: {},
    orgSchedule: [] as Record<string, unknown>[],
    orgScheduleLoading: false,
    orgScheduleError: null,
    showOrgScheduleEdit: false,
    activeIntegrationsLoading: false,
    activeIntegrations: [] as { integration_type: string, is_active: boolean }[],
    teams: [] as Record<string, unknown>[],
    teamsLoading: false,
    teamsError: null,
    teamsFetched: false,
    pendingInvites: [] as Record<string, unknown>[],
    pendingInvitesLoading: false,
    pendingInvitesError: null,
    revokeInviteLoading: false,
    revokeInviteError: null,
    revokeInviteSuccess: false,
    githubSettings: {},
    githubSettingsLoading: false,
    githubSettingsError: null,
    userPreferences: [] as UserPreferenceItem[],
    userPreferencesLoading: false,

    toleranceModes: [] as Record<string, unknown>[],
    toleranceModesLoading: false,
    toleranceModesError: null,
    slackSettingsLoading: false,
    slackSettings: null as SlackSettings | null,
    slackSettingsError: null as string | null,
    // Persisted long-running sync flags (cross-feature)
    azureStatesSyncRunning: false,
    jiraStatesSyncRunning: false
};

export const organizationSlice = createSlice({
    name: 'organization',
    initialState,
    reducers: {
        setOrganizationData: (state, action: PayloadAction<{
            items?: Record<string, unknown>[];
            projects?: Record<string, unknown>[];
            total?: number;
            pages?: number;
            page?: number;
            per_page?: number;
        }>) => {
            // Support both new API (items) and old API (projects) response formats
            state.data = action.payload.items || action.payload.projects || []
            state.totalItems = action.payload.total || 0
            state.totalPages = action.payload.pages || 1
            state.page = action.payload.page || 1
            state.perPage = action.payload.per_page || 10
            state.hasMore = state.page < state.totalPages
        },
        appendOrganizationData: (state, action: PayloadAction<{
            items?: Record<string, unknown>[];
            projects?: Record<string, unknown>[];
            total?: number;
            pages?: number;
            page?: number;
            per_page?: number;
        }>) => {
            // Support both new API (items) and old API (projects) response formats
            state.data = [...state.data, ...(action.payload.items || action.payload.projects || [])]
            state.totalItems = action.payload.total || 0
            state.totalPages = action.payload.pages || 1
            state.page = action.payload.page || 1
            state.perPage = action.payload.per_page || 10
            state.hasMore = state.page < state.totalPages
        },
        // Work Items reducers for combined API (fetchRoadmapsAndProjects)
        setWorkItemsData: (state, action: PayloadAction<{
            items?: Record<string, unknown>[];
            total?: number;
            pages?: number;
            page?: number;
            per_page?: number;
        }>) => {
            state.workItems = action.payload.items || []
            state.workItemsTotalItems = action.payload.total || 0
            state.workItemsTotalPages = action.payload.pages || 1
            state.workItemsPage = action.payload.page || 1
            state.workItemsPerPage = action.payload.per_page || 10
            state.workItemsHasMore = state.workItemsPage < state.workItemsTotalPages
        },
        appendWorkItemsData: (state, action: PayloadAction<{
            items?: Record<string, unknown>[];
            total?: number;
            pages?: number;
            page?: number;
            per_page?: number;
        }>) => {
            const newItems = action.payload.items || [];
            // Deduplicate: only append items not already in the list
            const existingIds = new Set(state.workItems.map((item: Record<string, unknown>) => item.id || item.project_id));
            const uniqueNewItems = newItems.filter((item: Record<string, unknown>) => !existingIds.has(item.id || item.project_id));
            state.workItems = [...state.workItems, ...uniqueNewItems]
            state.workItemsTotalItems = action.payload.total || 0
            state.workItemsTotalPages = action.payload.pages || 1
            state.workItemsPage = action.payload.page || 1
            state.workItemsPerPage = action.payload.per_page || 10
            state.workItemsHasMore = state.workItemsPage < state.workItemsTotalPages
        },
        setWorkItemsLoading: (state, action: PayloadAction<boolean>) => {
            state.workItemsLoading = action.payload
        },
        setWorkItemsLoadingMore: (state, action: PayloadAction<boolean>) => {
            state.workItemsLoadingMore = action.payload
        },
        clearWorkItemsData: (state) => {
            state.workItems = []
            state.workItemsPage = 1
            state.workItemsHasMore = true
            state.workItemsTotalItems = 0
            state.workItemsTotalPages = 1
        },
        setWorkItemsSearchTerm: (state, action: PayloadAction<string>) => {
            state.workItemsSearchTerm = action.payload
            state.workItemsPage = 1
            state.workItemsHasMore = true
        },
        setSearchTerm: (state, action: PayloadAction<string>) => {
            state.searchTerm = action.payload
            state.page = 1
            state.hasMore = true
        },
        setWorkItemsFilters: (state, action: PayloadAction<{
            searchType?: 'all' | 'roadmap' | 'project' | 'epic' | 'feature';
            sortBy?: string;
            sourceFilter?: string;
            quarterFilter?: string[];
            pinnedFilter?: boolean;
            priorityFilter?: string[];
        }>) => {
            if (action.payload.searchType !== undefined) {
                state.workItemsSearchType = action.payload.searchType;
            }
            if (action.payload.sortBy !== undefined) {
                state.workItemsSortBy = action.payload.sortBy;
            }
            if (action.payload.sourceFilter !== undefined) {
                state.workItemsSourceFilter = action.payload.sourceFilter;
            }
            if (action.payload.quarterFilter !== undefined) {
                state.workItemsQuarterFilter = action.payload.quarterFilter;
            }
            if (action.payload.pinnedFilter !== undefined) {
                state.workItemsPinnedFilter = action.payload.pinnedFilter;
            }
            if (action.payload.priorityFilter !== undefined) {
                state.workItemsPriorityFilter = action.payload.priorityFilter;
            }
        },
        setOrganizationOnboardSettings: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.onboardSettings = action.payload
        },
        setOnboardSettingsLoaded: (state, action: PayloadAction<boolean>) => {
            state.onboardSettingsLoaded = action.payload === true;
        },
        setOrgDetails: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.orgDetails = action.payload || {}
        },
        setCreateOrganizationData: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.orgdata = action.payload
        },
        setOrganizationLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setOrganizationLoadingMore: (state, action: PayloadAction<boolean>) => {
            state.loadingMore = action.payload
        },
        setOrganizationError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        resetOrganizationPagination: (state) => {
            state.page = 1
            state.hasMore = true
        },
        setOrganizationSkillData: (state, action: PayloadAction<{ skills: Record<string, unknown>[] }>) => {
            state.skills = action.payload.skills
        },
        setPinnedProjectsData: (state, action: PayloadAction<{ projects: GetPinnedProjectsResponse[] }>) => {
            state.pinnedProjects = action.payload.projects
            state.pinnedProjectsIds = action.payload.projects.map((project: GetPinnedProjectsResponse) => project.id)
        },

        setOrganizationSkillLoading: (state, action: PayloadAction<boolean>) => {
            state.skillLoading = action.payload
        },
        setRecentProjectsLoading: (state, action: PayloadAction<boolean>) => {
            state.recentProjectsLoading = action.payload
        },
        setOrganizationSkillError: (state, action: PayloadAction<string | null>) => {
            state.skillError = action.payload
        },
        setRecentProjectsError: (state, action: PayloadAction<string | null>) => {
            state.recentProjectsError = action.payload
            state.recentProjects = []
        },
        setOrganizationUpdateError: (state, action: PayloadAction<string | null>) => {
            state.orgUpdateError = action.payload
        },
        setOrganizationUpdateSuccess: (state, action: PayloadAction<boolean>) => {
            state.orgUpdateSuccess = action.payload
        },
        setOrganizationUpdateLoading: (state, action: PayloadAction<boolean>) => {
            state.orgUpdateLoading = action.payload
        },
        setShowSkillEdit: (state, action: PayloadAction<boolean>) => {
            state.showSkillEdit = action.payload
        },
        setInviteUsersLoading: (state, action: PayloadAction<boolean>) => {
            state.inviteUsersLoading = action.payload
        },
        setInviteUsersError: (state, action: PayloadAction<string | null>) => {
            state.inviteUsersError = action.payload
        },
        setInviteUsersSuccess: (state, action: PayloadAction<boolean>) => {
            state.inviteUsersSuccess = action.payload
        },
        setJiraSettingsGetSuccess: (state, action: PayloadAction<string | null>) => {
            state.jiraSettingsSuccess = action.payload
        },
        setJiraSettingsGetError: (state, action: PayloadAction<string | null>) => {
            state.jiraSettingsError = action.payload
        },
        setJiraSettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.jiraSettingsLoading = action.payload
        },
        setJiraSettingsSaveSuccess: (state, action: PayloadAction<string | null>) => {
            state.jiraSettingsSuccess = action.payload
        },
        setJiraSettingsSaveError: (state, action: PayloadAction<string | null>) => {
            state.jiraSettingsSaveError = action.payload
        },
        setProjectReferenceSaveError: (state, action: PayloadAction<string | null>) => {
            state.projectReferenceError = action.payload
        },
        setProjectReferenceLoading: (state, action: PayloadAction<boolean>) => {
            state.projectReferenceLoading = action.payload
        },
        projectReferenceRes: (state, action: PayloadAction<{ projects: Record<string, unknown>[] }>) => {
            state.projectReferenceData = action?.payload?.projects
        },
        setJiraSettings: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.jiraSettings = action.payload
        },
        setAsanaSettings: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.asanaSettings = action.payload
        },
        setAsanaSettingsGetSuccess: (state, action: PayloadAction<string | null>) => {
            state.asanaSettingsSuccess = action.payload
        },
        setAsanaSettingsGetError: (state, action: PayloadAction<string | null>) => {
            state.asanaSettingsError = action.payload
        },
        setAsanaSettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.asanaSettingsLoading = action.payload
        },
        setAsanaSettingsSaveSuccess: (state, action: PayloadAction<string | null>) => {
            state.asanaSettingsSuccess = action.payload
        },
        setAsanaSettingsSaveError: (state, action: PayloadAction<string | null>) => {
            state.asanaSettingsSaveError = action.payload
        },
        setOrgPreferencesLoading: (state, action: PayloadAction<boolean>) => {
            state.orgPreferencesLoading = action.payload
        },
        orgPreferencesRes: (state, action: PayloadAction<Preference[]>) => {
            state.orgPreferences = action.payload
        },
        addOrgPreference: (state, action: PayloadAction<Preference>) => {
            state.orgPreferences.push(action.payload);
        },
        removeOrgPreference: (state, action: PayloadAction<number>) => {
            state.orgPreferences = state.orgPreferences.filter(
                (p) => p.pref_id !== action.payload
            );
        },
        updatePreferenceId: (state, action: PayloadAction<{
            oldId: number; newId: number
        }>) => {
            const { oldId, newId } = action.payload;
            const preference = state.orgPreferences.find(el => el.pref_id === oldId);
            if (preference) {
                preference.pref_id = newId;
            }
        },
        setOrgPreferencesError: (state, action: PayloadAction<string | null>) => {
            state.orgPreferencesError = action.payload
        },
        setOrgWorkingDays: (state, action: PayloadAction<Record<string, unknown>[]>) => {
            state.orgWorkingDays = action.payload
        },
        setOrgWorkingDaysLoading: (state, action: PayloadAction<boolean>) => {
            state.orgWorkingDaysLoading = action.payload
        },
        setAzureSettingsGetSuccess: (state, action: PayloadAction<string | null>) => {
            state.azureSettingsSuccess = action.payload
        },
        setAzureSettingsGetError: (state, action: PayloadAction<string | null>) => {
            state.azureSettingsError = action.payload
        },
        setAzureSettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.azureSettingsLoading = action.payload
        },
        setAzureSettingsSaveSuccess: (state, action: PayloadAction<string | null>) => {
            state.azureSettingsSuccess = action.payload
        },
        setAzureSettingsSaveError: (state, action: PayloadAction<string | null>) => {
            state.azureSettingsSaveError = action.payload
        },
        setAzureSettings: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.azureSettings = action.payload
        },
        setMondaySettingsGetSuccess: (state, action: PayloadAction<string | null>) => {
            state.mondaySettingsSuccess = action.payload
        },
        setMondaySettingsGetError: (state, action: PayloadAction<string | null>) => {
            state.mondaySettingsError = action.payload
        },
        setMondaySettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.mondaySettingsLoading = action.payload
        },
        setMondaySettingsSaveSuccess: (state, action: PayloadAction<string | null>) => {
            state.mondaySettingsSuccess = action.payload
        },
        setMondaySettingsSaveError: (state, action: PayloadAction<string | null>) => {
            state.mondaySettingsSaveError = action.payload
        },
        setMondaySettings: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.mondaySettings = action.payload
        },
        setOrgScheduleData: (state, action: PayloadAction<{ holidays: Record<string, unknown>[] }>) => {
            state.orgSchedule = action.payload.holidays
        },
        setOrgScheduleLoading: (state, action: PayloadAction<boolean>) => {
            state.orgScheduleLoading = action.payload
        },
        setOrgScheduleError: (state, action: PayloadAction<string | null>) => {
            state.orgScheduleError = action.payload
        },
        setshoworgScheduleEdit: (state, action: PayloadAction<boolean>) => {
            state.showOrgScheduleEdit = action.payload
        },
        setActiveIntegrationsLoading: (state, action: PayloadAction<boolean>) => {
            state.activeIntegrationsLoading = action.payload;
        },
        setActiveIntegrations: (state, action: PayloadAction<{ integration_type: string; is_active: boolean }[]>) => {
            state.activeIntegrations = action.payload;
        },
        setTeamsLoading: (state, action: PayloadAction<boolean>) => {
            state.teamsLoading = action.payload;
            // When we start loading, mark as "attempted" to prevent duplicate calls
            if (action.payload === true) {
                state.teamsFetched = true;
            }
        },
        setTeamsError: (state, action: PayloadAction<string | null>) => {
            // Only store serializable error information
            state.teamsError = action.payload || null;
            // Reset teamsFetched on error so it can be retried
            state.teamsFetched = false;
        },
        setTeams: (state, action: PayloadAction<Record<string, unknown>[]>) => {
            state.teams = action.payload;
            state.teamsFetched = true;
        },
        resetTeamsFetched: (state) => {
            state.teamsFetched = false;
        },
        setPendingInvitesLoading: (state, action: PayloadAction<boolean>) => {
            state.pendingInvitesLoading = action.payload;
        },
        setPendingInvitesError: (state, action: PayloadAction<string | null>) => {
            state.pendingInvitesError = action.payload;
        },
        setPendingInvites: (state, action: PayloadAction<Record<string, unknown>[]>) => {
            state.pendingInvites = action.payload;
        },
        setRevokeInviteLoading: (state, action: PayloadAction<boolean>) => {
            state.revokeInviteLoading = action.payload;
        },
        setRevokeInviteError: (state, action: PayloadAction<string | null>) => {
            state.revokeInviteError = action.payload;
        },
        setRevokeInviteSuccess: (state, action: PayloadAction<boolean>) => {
            state.revokeInviteSuccess = action.payload;
        },
        setGithubSettings: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.githubSettings = action.payload;
        },
        setGithubSettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.githubSettingsLoading = action.payload;
        },
        setGithubSettingsError: (state, action: PayloadAction<string | null>) => {
            state.githubSettingsError = action.payload;
        },
        setUserPreferencesLoading: (state, action: PayloadAction<boolean>) => {
            state.userPreferencesLoading = action.payload;
        },
        setUserPreferences: (state, action: PayloadAction<UserPreferenceItem[]>) => {
            state.userPreferences = action.payload;
        },
        removeUserPreference: (state, action: PayloadAction<number>) => {
            state.userPreferences = state.userPreferences.filter(
                (pref) => pref.id !== action.payload
            );
        },
        setSlackSettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.slackSettingsLoading = action.payload;
        },
        setSlackSettings: (state, action: PayloadAction<SlackSettings | null>) => {
            state.slackSettings = action.payload || null;
        },
        setSlackSettingsError: (state, action: PayloadAction<string | null>) => {
            state.slackSettingsError = action.payload;
        },
        // States sync flags
        setAzureStatesSyncRunning: (state, action: PayloadAction<boolean>) => {
            state.azureStatesSyncRunning = action.payload;
        },
        setJiraStatesSyncRunning: (state, action: PayloadAction<boolean>) => {
            state.jiraStatesSyncRunning = action.payload;
        },
        setToleranceModes: (state, action: PayloadAction<Record<string, unknown>[]>) => {
            state.toleranceModes = action.payload;
        },
        setToleranceModesLoading: (state, action: PayloadAction<boolean>) => {
            state.toleranceModesLoading = action.payload;
        },
        setToleranceModesError: (state, action: PayloadAction<string | null>) => {
            state.toleranceModesError = action.payload;
        }
    }
});

export const {
    setOrganizationData,
    appendOrganizationData,
    setOrganizationLoadingMore,
    resetOrganizationPagination,
    //Work Items actions
    setWorkItemsData,
    appendWorkItemsData,
    setWorkItemsLoading,
    setWorkItemsLoadingMore,
    clearWorkItemsData,
    setWorkItemsSearchTerm,
    setCreateOrganizationData,
    setOrganizationLoading,
    setOrganizationError,
    setOrganizationSkillLoading,
    setOrganizationSkillError,
    setOrganizationSkillData,
    setOrganizationUpdateError,
    setOrganizationUpdateSuccess,
    setOrganizationUpdateLoading,
    setShowSkillEdit,
    setRecentProjectsLoading,
    setPinnedProjectsData,
    setRecentProjectsError,
    setInviteUsersLoading,
    setInviteUsersError,
    setInviteUsersSuccess,
    setJiraSettingsGetSuccess,
    setJiraSettingsLoading,
    setJiraSettingsGetError,
    setJiraSettingsSaveSuccess,
    setJiraSettingsSaveError,
    setJiraSettings,
    setProjectReferenceSaveError,
    setProjectReferenceLoading,
    projectReferenceRes,
    setOrganizationOnboardSettings,
    setOnboardSettingsLoaded,
    setOrgDetails,
    setAsanaSettingsGetSuccess,
    setAsanaSettingsLoading,
    setAsanaSettingsGetError,
    setAsanaSettingsSaveSuccess,
    setAsanaSettingsSaveError,
    setAsanaSettings,
    setOrgPreferencesError,
    setOrgPreferencesLoading,
    orgPreferencesRes,
    setOrgWorkingDays,
    setOrgWorkingDaysLoading,
    setAzureSettingsGetSuccess,
    setAzureSettingsLoading,
    setAzureSettingsGetError,
    setAzureSettingsSaveSuccess,
    setAzureSettingsSaveError,
    setAzureSettings,
    setMondaySettingsGetSuccess,
    setMondaySettingsLoading,
    setMondaySettingsGetError,
    setMondaySettingsSaveSuccess,
    setMondaySettingsSaveError,
    setMondaySettings,
    setOrgScheduleData,
    setOrgScheduleLoading,
    setOrgScheduleError,
    setshoworgScheduleEdit,
    setSearchTerm,
    setWorkItemsFilters,
    setActiveIntegrations,
    setActiveIntegrationsLoading,
    setTeamsLoading,
    setTeamsError,
    setTeams,
    resetTeamsFetched,
    setPendingInvitesLoading,
    setPendingInvitesError,
    setPendingInvites,
    setRevokeInviteLoading,
    setRevokeInviteError,
    setRevokeInviteSuccess,
    addOrgPreference,
    removeOrgPreference,
    updatePreferenceId,
    setGithubSettings,
    setGithubSettingsLoading,
    setGithubSettingsError,
    setUserPreferencesLoading,
    setUserPreferences,
    removeUserPreference,
    setSlackSettingsLoading,
    setSlackSettings,
    setSlackSettingsError,
    setAzureStatesSyncRunning,
    setJiraStatesSyncRunning,
    setToleranceModes,
    setToleranceModesLoading,
    setToleranceModesError
} = organizationSlice.actions;

export default organizationSlice.reducer;
